import React, {useCallback, useContext, useMemo, useState} from 'react';

import {useAdminAccessRight} from 'src/hooks/access-rights';
import {DEFAULT_PAGE_SIZES} from 'src/hooks/common-data-service/cached-table';
import {
    getSubscriptionSeatText,
} from 'src/modules/profile/account/tabs/account-profile/product-access-summary-section/subscription-seat-text';
import {COLUMNS} from 'src/modules/profile/account/tabs/product-access/constant';
import {formatLicenseDate} from 'src/modules/profile/utils/formatLicenseDate';
import {ProductSubscriptionCapacityStatus} from 'src/openapi/admin/models/ProductSubscriptionCapacityStatus';
import {
    ProductSubscriptionLifecycleStatus,
} from 'src/openapi/admin/models/ProductSubscriptionLifecycleStatus';
import {
    ProductSubscriptionSortDirection,
    ProductSubscriptionSortField,
} from 'src/openapi/admin/models/ProductSubscriptionSort';

import {FlatButton} from '@pb/kit-component-button';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {Dropdown} from '@pb/kit-component-dropdown';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Tooltip} from '@pb/kit-component-popups';
import {SortHeaderWithDataTable} from '@pb/kit-component-sort-header';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';
import {colors} from '@pb/kit-stylus/config.json';

import {
    sectionCapacityWarning,
} from '../account-profile/product-access-summary-section/product-access-summary-section.module.styl';

import {EditAccessTermModal} from './edit-access-term-modal';
import {formatSubscriptionEnumLabel} from './subscription-enum-label';

import type {ProductSubscription} from 'src/openapi/admin/models/ProductSubscription';

type Props = {
    subscriptions: ProductSubscription[],
    totalCount: number,
    page: number,
    pageSize: number,
    sort: {field: ProductSubscriptionSortField, direction: ProductSubscriptionSortDirection},
    statuses: ProductSubscriptionLifecycleStatus[],
    onPageChange: (page: number) => void,
    onPageSizeChange: (pageSize: number) => void,
    onSortChange: (field: ProductSubscriptionSortField, direction: ProductSubscriptionSortDirection) => void,
    onUpdated: () => void,
    accountId: number,
};

export const ProductAccessTable: React.FC<Props> = (
    {
        subscriptions,
        totalCount,
        page,
        pageSize,
        sort,
        statuses,
        onPageChange,
        onPageSizeChange,
        onSortChange,
        onUpdated,
        accountId,
    }) => {
    const {locale} = useContext(LocaleContext);
    const [editedSubscription, setEditedSubscription] = useState<ProductSubscription | null>(null);
    const [busy, setBusy] = useState(false);
    const columns = useMemo(
        () => COLUMNS.filter(column =>
            column.columnId !== 'action' ||
            statuses.length === 0 ||
            statuses.includes(ProductSubscriptionLifecycleStatus.ACTIVE) ||
            statuses.includes(ProductSubscriptionLifecycleStatus.PENDING),
        ),
        [statuses],
    );
    const rows = useMemo(() => subscriptions.map(subscription => ({
        id: subscription.productSubscriptionId,
        columnValues: {
            product: subscription.product.name,
            status: formatSubscriptionEnumLabel(subscription.lifecycleStatus),
            sourceRefs: subscription.sourceRefs.map(ref => ref.referenceId).join(', '),
            startDate: formatLicenseDate(locale.dateFormat, subscription.startDate),
            endDate: formatLicenseDate(locale.dateFormat, subscription.endDate),
            seats: getSubscriptionSeatText(subscription),
            source: formatSubscriptionEnumLabel(subscription.source),
        } as Record<string, string>,
    })), [locale.dateFormat, subscriptions]);

    const closeEditModal = useCallback(() => {
        if (!busy) {
            setEditedSubscription(null);
        }
    }, [busy]);

    const onEditSuccess = useCallback(() => {
        setBusy(false);
        setEditedSubscription(null);
        onUpdated();
    }, [onUpdated]);

    const {canEditAccountProducts} = useAdminAccessRight();

    const canEdit = useMemo(() => canEditAccountProducts(accountId), [canEditAccountProducts, accountId]);

    return (
        <>
            <Table fixed={true}>
                <Head>
                    <Row>
                        {columns.map(col => {
                            const sortField = col.columnId === ProductSubscriptionSortField.startDate ||
                                col.columnId === ProductSubscriptionSortField.endDate
                                ? col.columnId as ProductSubscriptionSortField
                                : null;

                            return (
                                <Cell key={col.columnId} as="th" scope="col" width={col.width} border={true}>
                                    {sortField ? (
                                        <SortHeaderWithDataTable
                                            defaultDirection={ProductSubscriptionSortDirection.ASC}
                                            direction={sort.field === sortField ? sort.direction : null}
                                            filterActive={true}
                                            caption={col.caption}
                                            onSort={direction =>
                                                onSortChange(sortField, direction as ProductSubscriptionSortDirection)}
                                        />
                                    ) : <strong>{col.caption}</strong>}
                                </Cell>
                            );
                        })}
                    </Row>
                </Head>
                <Body>
                    {rows.length > 0 ? (
                        rows.map((row, index) => (
                            <Row key={row.id} showZebra={index % 2 === 0}>
                                {columns.map(col => {
                                    const subscription = subscriptions[index];
                                    const isEditable = canEdit &&
                                        subscription.source === 'MANUAL' &&
                                        (subscription.lifecycleStatus === 'PENDING' || subscription.lifecycleStatus === 'ACTIVE');
                                    const isOverCapacity =
                                        subscription.capacityStatus === ProductSubscriptionCapacityStatus.OVER_CAPACITY;
                                    let content: React.ReactNode = String(row.columnValues[col.columnId] || '--');

                                    if (col.columnId === 'action') {
                                        content = null;
                                    }
                                    if (col.columnId === 'seats') {
                                        content = (
                                            <span>{row.columnValues.seats}</span>
                                        );
                                    }
                                    if (col.columnId === 'sourceRefs') {
                                        content = row.columnValues.sourceRefs ? (
                                            <Tooltip
                                                color="dark"
                                                anchor={({anchorRef, getAnchorProps}) => (
                                                    <span ref={anchorRef} {...getAnchorProps()}>
                                                        {row.columnValues.sourceRefs}
                                                    </span>
                                                )}>
                                                {row.columnValues.sourceRefs}
                                            </Tooltip>
                                        ) : '--';
                                    }
                                    if (col.columnId === 'seats' && isOverCapacity) {
                                        content = (
                                            <Group gap="s" alignItems="center">
                                                <span style={{color: colors.otherColors.error}}>
                                                    <span>{row.columnValues.seats}</span>
                                                </span>
                                                <Dropdown
                                                    hoverEnabled={true}
                                                    role="tooltip"
                                                    anchor={({anchorRef, getAnchorProps}) => (
                                                        <span
                                                            ref={anchorRef}
                                                            {...getAnchorProps()}
                                                            aria-label="Subscription requires manual involvement">
                                                            <Icon
                                                                type={Icons.WARNING}
                                                                className={sectionCapacityWarning}
                                                            />
                                                        </span>
                                                    )}>
                                                    <span>Seats over capacity. Manual involvement needed</span>
                                                </Dropdown>
                                            </Group>
                                        );
                                    }
                                    if (col.columnId === 'action' && isEditable) {
                                        content = (
                                            <FlatButton
                                                startIcon={<Icon type={Icons.EDIT}/>}
                                                onClick={() => setEditedSubscription(subscription)}>
                                                <b>Edit</b>
                                            </FlatButton>
                                        );
                                    }

                                    return (
                                        <Cell key={`${row.id}-${col.columnId}`}>
                                            {content}
                                        </Cell>
                                    );
                                })}
                            </Row>
                        ))
                    ) : (
                        <Row>
                            <Cell colSpan={columns.length} style={{textAlign: 'center'}}>
                                <div style={{
                                    height: '400px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    Sorry, your search has no results for the specified criteria
                                </div>
                            </Cell>
                        </Row>
                    )}
                </Body>
            </Table>
            <div style={{margin: 16}}>
                <PaginationStripe
                    goToPage={true}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    currentPage={page}
                    onPaginationChange={pagination => onPageChange(pagination.currentPage)}
                    onChangePaginationSize={(_currentPage, nextPageSize) => onPageSizeChange(nextPageSize)}
                    sizeSwitcherPosition="LEFT"
                    sizeSwitcherType="ONLY_PAGE_SIZE"
                    sizes={DEFAULT_PAGE_SIZES}
                />
            </div>
            <Dialog
                shown={Boolean(editedSubscription)}
                setShown={value => {
                    if (!value) {
                        closeEditModal();
                    }
                }}
                wrapperProps={{style: {width: 550}}}>
                {editedSubscription ? (
                    <EditAccessTermModal
                        subscription={editedSubscription}
                        onBusyChange={setBusy}
                        onClose={closeEditModal}
                        onUpdated={onEditSuccess}
                    />
                ) : null}
            </Dialog>
        </>
    );
};
